import { Variant } from "types/questionnaire.types"

/**
 * Parses the questionnaire text to extract variants.
 *
 * This function processes the questionnaire text, extracting different variants defined
 * by `#DEFINE VARIANT` lines and their associated content. Each variant includes its name,
 * languages, and content.
 *
 * @param text - The questionnaire text to parse
 * @returns An array of variant objects. Each object contains the variant's name, languages, and content
 */
export const parseQuestionnaireVariant = (text: string): Variant[] => {
  const lines: string[] = text.split("\n")
  const variants: Variant[] = []
  let currentVariant: Variant | null = null
  let currentContent: string[] = []

  lines.forEach((line: string) => {
    const defineVariantMatch: RegExpMatchArray | null = line.match(
      /^#DEFINE VARIANT (.+)/
    ) // Capture everything after #DEFINE VARIANT
    const languageMatch: RegExpMatchArray | null = line.match(/^#LANGUAGE (.+)/) // Check if we encounter #DEFINE VARIANT
    if (defineVariantMatch) {
      if (currentVariant) {
        ;(currentVariant as Variant).content = currentContent.join("\n").trim()
        variants.push(currentVariant)
      }
      currentVariant = {
        name: defineVariantMatch[1].trim(), // Trim any excess whitespace
        languages: [],
        content: "",
      }
      currentContent = [] // Reset the content, excluding the define line
    } // Check if we encounter #LANGUAGE
    else if (languageMatch && currentVariant) {
      ;(currentVariant as Variant).languages = languageMatch[1]
        .split(",")
        .map((lang: string) => lang.trim())
      currentContent.push("") // Ensuring a blank line after language line
    }
    // Check if the line is not #Line 0 before adding it to the content
    else if (currentVariant && !line.startsWith("#LINE 0")) {
      currentContent.push(line)
    }
  }) // Ensure to add the last variant if the loop ends
  if (currentVariant) {
    ;(currentVariant as Variant).content = currentContent.join("\n").trim()
    variants.push(currentVariant)
  }

  return variants
}

/**
 * The questionnaire's own languages, which Master uses: those of the first `#LANGUAGE` line above any
 * `#DEFINE VARIANT` line, split on commas and trimmed, or none when there is no such line.
 * `#DESIGN LANGUAGE` is not a `#LANGUAGE` line.
 *
 * @param text - The questionnaire text
 * @returns The languages in the order written, or an empty array
 */
export const parseMasterLanguages = (text: string): string[] => {
  for (const line of text.split(/\r?\n/)) {
    if (/^\s*#DEFINE VARIANT\b/i.test(line)) return []
    const languages = /^\s*#LANGUAGE\b(.*)$/i.exec(line)
    if (languages)
      return languages[1]
        .split(",")
        .map((lang: string) => lang.trim())
        .filter(Boolean)
  }
  return []
}

/**
 * Updates the questionnaire with the variant content.
 *
 * @param updatedQuestionnaire - The original questionnaire
 * @param selectedVariantName - The name of the selected variant
 * @param text - The variant content
 * @param languages - Array of languages for the variant (optional)
 * @returns The updated questionnaire
 */
export const updateQuestionnaireWithVariant = (
  updatedQuestionnaire: string,
  selectedVariantName: string,
  text: string,
  languages: string[] = []
): string => {
  const defineLine: string = `#DEFINE VARIANT ${selectedVariantName}`
  const languageLine: string = languages.length
    ? `#LANGUAGE ${languages.join(", ")}`
    : ""

  // Ensure that the variant content is surrounded by proper newlines
  const variantContent: string =
    `\n${defineLine}\n${languageLine}\n#LINE 0\n${text.trim()}\n`.trim()

  // Use regular expression to find and replace the existing variant block
  const variantRegex: RegExp = new RegExp(
    `(#DEFINE VARIANT ${selectedVariantName}\\s*[\\s\\S]*?)(?=#DEFINE VARIANT|$)`,
    "g"
  )

  if (variantRegex.test(updatedQuestionnaire)) {
    return updatedQuestionnaire.replace(variantRegex, `\n${variantContent}\n`)
  } else {
    // Append the new variant content to the questionnaire, ensuring proper newline separation
    return `${updatedQuestionnaire.trim()}\n\n${variantContent}\n`.trim()
  }
}
